import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { AuthProvider } from './utils/auth';
import { PrivateRoute, AdminRoute } from './components/ProtectedRoutes';
import AdminLayout from './components/AdminLayout';
import useSiteSettings from './hooks/useSiteSettings';

// 路由级别懒加载 —— 每个页面独立 chunk，按需下载
// 员工端只加载员工页面，管理端只加载管理页面
const Login = lazy(() => import('./pages/Login'));
const Learn = lazy(() => import('./pages/Learn'));
const Complete = lazy(() => import('./pages/Complete'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const UserManage = lazy(() => import('./pages/admin/UserManage'));
const DepartmentManage = lazy(() => import('./pages/admin/DepartmentManage'));
const ContentManage = lazy(() => import('./pages/admin/ContentManage'));
const RatingItemManage = lazy(() => import('./pages/admin/RatingItemManage'));
const QuestionManage = lazy(() => import('./pages/admin/QuestionManage'));
const FillBlankManage = lazy(() => import('./pages/admin/FillBlankManage'));
const Statistics = lazy(() => import('./pages/admin/Statistics'));
const LoginLog = lazy(() => import('./pages/admin/LoginLog'));
const PromiseManage = lazy(() => import('./pages/admin/PromiseManage'));
const SiteSettings = lazy(() => import('./pages/admin/SiteSettings'));
const SecuritySettings = lazy(() => import('./pages/admin/SecuritySettings'));

// 统一的加载占位
const PageLoading = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin size="large" tip="加载中..." />
  </div>
);

const AppInit = () => {
  const settings = useSiteSettings();

  useEffect(() => {
    if (!settings) return;
    const title = settings.title || settings.name || '十六字质量方针宣贯系统';
    document.title = title;

    let icoLink = document.getElementById('dynamic-favicon');
    if (settings.ico) {
      if (!icoLink) {
        icoLink = document.createElement('link');
        icoLink.id = 'dynamic-favicon';
        icoLink.rel = 'icon';
        document.head.appendChild(icoLink);
      }
      icoLink.href = `/${settings.ico}`;
    }
  }, [settings]);

  return null;
};

function App() {
  return (
    <ConfigProvider locale={zhCN}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <AppInit />
          <Suspense fallback={<PageLoading />}>
            <Routes>
              {/* 员工端路由 */}
              <Route path="/login" element={<Login />} />
              <Route path="/learn" element={
                <PrivateRoute>
                  <Learn />
                </PrivateRoute>
              } />
              <Route path="/complete" element={
                <PrivateRoute>
                  <Complete />
                </PrivateRoute>
              } />

              {/* 管理后台路由 */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<Navigate to="/admin/statistics" replace />} />
              <Route element={
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              }>
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/users" element={<UserManage />} />
                <Route path="/admin/departments" element={<DepartmentManage />} />
                <Route path="/admin/contents" element={<ContentManage />} />
                <Route path="/admin/rating-items" element={<RatingItemManage />} />
                <Route path="/admin/questions" element={<QuestionManage />} />
                <Route path="/admin/fillblanks" element={<FillBlankManage />} />
                <Route path="/admin/statistics" element={<Statistics />} />
                <Route path="/admin/promises" element={<PromiseManage />} />
                <Route path="/admin/settings" element={<SiteSettings />} />
                <Route path="/admin/security" element={<SecuritySettings />} />
                <Route path="/admin/login-logs" element={<LoginLog />} />
              </Route>

              {/* 默认路由 */}
              <Route path="/" element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </Router>
    </ConfigProvider>
  );
}

export default App;
